import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const StaffDirectory = ({ staffMembers = [], setStaffMembers, providers = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'create', 'edit', 'approve'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    merchantName: '',
    permissions: [],
    status: 'pending'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Available permissions
  const availablePermissions = [
    'order_management',
    'customer_support',
    'inventory_management',
    'financial_reports',
    'user_management',
    'analytics_view',
    'system_settings'
  ];

  // Available roles and departments
  const availableRoles = [
    'Administrator',
    'Manager', 
    'Customer Service',
    'Operations Specialist',
    'Financial Analyst',
    'Support Staff'
  ];

  const availableDepartments = [
    'Administration',
    'Operations',
    'Customer Service',
    'Finance',
    'Technology',
    'Marketing'
  ];

  // Get available merchants from providers
  const availableMerchants = [
    'Central Office',
    'Regional Hub A',
    'Regional Hub B',
    'Branch Office 1',
    'Branch Office 2',
    ...(providers?.map(p => p?.businessName || p?.name)?.filter(Boolean) || [])
  ];

  // Filter staff based on search and status
  const filteredStaff = staffMembers?.filter(staff => {
    const matchesSearch = staff?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         staff?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         staff?.merchantName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || staff?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleStatusChange = (staffId, newStatus) => {
    setStaffMembers(prev => prev?.map(staff => 
      staff?.id === staffId ? { ...staff, status: newStatus, updatedAt: new Date()?.toISOString() } : staff
    ));
  };

  const handleCreateStaff = () => {
    setSelectedStaff(null);
    setModalType('create');
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: '',
      department: '',
      merchantName: '',
      permissions: [],
      status: 'pending'
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleEditStaff = (staff) => {
    setSelectedStaff(staff);
    setModalType('edit');
    setFormData({
      name: staff?.name || '',
      email: staff?.email || '',
      phone: staff?.phone || '',
      role: staff?.role || '',
      department: staff?.department || '',
      merchantName: staff?.merchantName || '',
      permissions: staff?.permissions || [],
      status: staff?.status || 'pending'
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleApproveStaff = (staff) => {
    setSelectedStaff(staff);
    setModalType('approve');
    setIsModalOpen(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePermissionToggle = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev?.permissions?.includes(permission)
        ? prev?.permissions?.filter(p => p !== permission)
        : [...prev?.permissions, permission]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData?.department) {
      newErrors.department = 'Department is required';
    }

    if (!formData?.merchantName) {
      newErrors.merchantName = 'Merchant assignment is required';
    }

    if (formData?.permissions?.length === 0) {
      newErrors.permissions = 'At least one permission must be selected';
    }

    setErrors(newErrors);
    return Object?.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const newStaff = {
        id: modalType === 'edit' ? selectedStaff?.id : `staff-${Date?.now()}`,
        ...formData,
        hireDate: modalType === 'edit' ? selectedStaff?.hireDate : new Date()?.toISOString()?.split('T')?.[0],
        lastLogin: modalType === 'edit' ? selectedStaff?.lastLogin : null,
        createdAt: modalType === 'edit' ? selectedStaff?.createdAt : new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      };

      if (modalType === 'edit') {
        setStaffMembers(prev => 
          prev?.map(staff => staff?.id === selectedStaff?.id ? newStaff : staff)
        );
      } else {
        setStaffMembers(prev => [newStaff, ...prev]);
      }

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsModalOpen(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
        department: '',
        merchantName: '',
        permissions: [],
        status: 'pending'
      });
      setErrors({});
    } catch (error) {
      console?.error('Error saving staff:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproval = (approved) => {
    if (approved) {
      handleStatusChange(selectedStaff?.id, 'active');
    } else {
      handleStatusChange(selectedStaff?.id, 'inactive');
    }
    setIsModalOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-orange-600 bg-orange-50';
      case 'inactive': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const stats = {
    total: staffMembers?.length || 0,
    active: staffMembers?.filter(staff => staff?.status === 'active')?.length || 0,
    pending: staffMembers?.filter(staff => staff?.status === 'pending')?.length || 0,
    inactive: staffMembers?.filter(staff => staff?.status === 'inactive')?.length || 0
  };

  return (
    <div className="flex-1 overflow-hidden bg-background">
      {/* Header Section */}
      <div className="bg-card border-b border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Staff Directory</h2>
            <p className="text-sm text-muted-foreground">Manage staff accounts and merchant mappings</p>
          </div>
          <Button onClick={handleCreateStaff} className="flex items-center space-x-2">
            <Icon name="Plus" size={16} />
            <span>Add Staff</span>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-foreground">{stats?.total}</div>
            <div className="text-sm text-muted-foreground">Total Staff</div>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{stats?.active}</div>
            <div className="text-sm text-muted-foreground">Active</div>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-600">{stats?.pending}</div>
            <div className="text-sm text-muted-foreground">Pending</div>
          </div>
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{stats?.inactive}</div>
            <div className="text-sm text-muted-foreground">Inactive</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search staff by name, email, or merchant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      {/* Staff List */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          {filteredStaff?.map((staff) => (
            <div key={staff?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{staff?.name}</h3>
                    <p className="text-sm text-muted-foreground">{staff?.email}</p>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-sm text-muted-foreground">
                        <Icon name="Briefcase" size={14} className="inline mr-1" />
                        {staff?.role} • {staff?.department}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        <Icon name="Building" size={14} className="inline mr-1" />
                        {staff?.merchantName}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(staff?.status)}`}>
                    {staff?.status?.charAt(0)?.toUpperCase() + staff?.status?.slice(1)}
                  </span>
                  <div className="flex items-center space-x-2">
                    {staff?.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleApproveStaff(staff)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Approve
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditStaff(staff)}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(staff?.id, staff?.status === 'active' ? 'inactive' : 'active')}
                      className={staff?.status === 'active' ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                    >
                      <Icon name={staff?.status === 'active' ? 'UserX' : 'UserCheck'} size={16} />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Additional Info */}
              <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Phone:</span>
                  <span className="ml-2 text-foreground">{staff?.phone}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Hire Date:</span>
                  <span className="ml-2 text-foreground">{staff?.hireDate}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Login:</span>
                  <span className="ml-2 text-foreground">{staff?.lastLogin || 'Never'}</span>
                </div>
              </div>

              {/* Permissions */}
              <div className="mt-3">
                <span className="text-sm text-muted-foreground">Permissions:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {staff?.permissions?.map((permission, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                      {permission?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredStaff?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No staff members found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Get started by adding your first staff member.'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Button onClick={handleCreateStaff}>
                <Icon name="Plus" size={16} className="mr-2" />
                Add First Staff Member
              </Button>
            )}
          </div>
        )}
      </div>
      {/* Modal for Create/Edit/Approve */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {modalType === 'approve' ? (
              // Approval Modal
              (<>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Approve Staff Member
                </h3>
                <div className="mb-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={32} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-foreground">{selectedStaff?.name}</h4>
                      <p className="text-sm text-muted-foreground">{selectedStaff?.email}</p>
                      <p className="text-sm text-muted-foreground">{selectedStaff?.role} • {selectedStaff?.department}</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    Do you want to approve this staff member and grant them access to the system?
                  </p>
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleApproval(false)}
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleApproval(true)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    Approve
                  </Button>
                </div>
              </>)
            ) : (
              // Create/Edit Form Modal
              (<>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  {modalType === 'create' ? 'Add New Staff Member' : 'Edit Staff Member'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Full Name"
                      value={formData?.name}
                      onChange={(e) => handleInputChange('name', e?.target?.value)}
                      placeholder="Enter full name"
                      error={errors?.name}
                      required
                    />
                    <Input
                      label="Email Address"
                      type="email"
                      value={formData?.email}
                      onChange={(e) => handleInputChange('email', e?.target?.value)}
                      placeholder="Enter email address"
                      error={errors?.email}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Phone Number"
                      value={formData?.phone}
                      onChange={(e) => handleInputChange('phone', e?.target?.value)}
                      placeholder="Enter phone number"
                      error={errors?.phone}
                      required
                    />
                    
                    {/* Role Dropdown */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData?.role}
                        onChange={(e) => handleInputChange('role', e?.target?.value)}
                        className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select a role</option>
                        {availableRoles?.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      {errors?.role && (
                        <p className="text-sm text-red-500">{errors?.role}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Department Dropdown */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData?.department}
                        onChange={(e) => handleInputChange('department', e?.target?.value)}
                        className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select a department</option>
                        {availableDepartments?.map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                      {errors?.department && (
                        <p className="text-sm text-red-500">{errors?.department}</p>
                      )}
                    </div>

                    {/* Merchant Assignment */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Merchant Assignment <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData?.merchantName}
                        onChange={(e) => handleInputChange('merchantName', e?.target?.value)}
                        className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select merchant</option>
                        {availableMerchants?.map(merchant => (
                          <option key={merchant} value={merchant}>{merchant}</option>
                        ))}
                      </select>
                      {errors?.merchantName && (
                        <p className="text-sm text-red-500">{errors?.merchantName}</p>
                      )}
                    </div>
                  </div>

                  {/* Permissions */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Permissions <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {availablePermissions?.map(permission => (
                        <label key={permission} className="flex items-center space-x-2 p-2 border border-border rounded-lg hover:bg-accent/50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData?.permissions?.includes(permission)}
                            onChange={() => handlePermissionToggle(permission)}
                            className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                          />
                          <span className="text-sm text-foreground">
                            {permission?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors?.permissions && (
                      <p className="text-sm text-red-500">{errors?.permissions}</p>
                    )}
                  </div>

                  {modalType === 'edit' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Status</label>
                      <select
                        value={formData?.status}
                        onChange={(e) => handleInputChange('status', e?.target?.value)}
                        className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsModalOpen(false)}
                      className="flex-1"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : modalType === 'create' ? 'Add Staff Member' : 'Update Staff Member'}
                    </Button>
                  </div>
                </form>
              </>)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffDirectory;